import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { api } from '../../services/api';

function AdminPrograms() {
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchPrograms();
  }, []);

  useEffect(() => {
    // Filter programs based on search query
    if (searchQuery.trim() === '') {
      setFilteredPrograms(programs);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = programs.filter(program =>
        program.name.toLowerCase().includes(query) ||
        program.term.toLowerCase().includes(query) ||
        program.location.toLowerCase().includes(query)
      );
      setFilteredPrograms(filtered);
    }
  }, [searchQuery, programs]);

  const fetchPrograms = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await api.getPrograms();
      setPrograms(data || []);
      setFilteredPrograms(data || []);
    } catch (error) {
      console.error('Error fetching programs:', error);
      setError(error.response?.data?.error || 'Failed to load programs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (programId, programName) => {
    if (!window.confirm(`Are you sure you want to delete "${programName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.deleteProgram(programId);
      setPrograms(programs.filter(p => p.id !== programId));
      setFilteredPrograms(filteredPrograms.filter(p => p.id !== programId));
    } catch (error) {
      console.error('Error deleting program:', error);
      alert(error.response?.data?.error || 'Failed to delete program');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading programs...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error">{error}</Alert>
          <Button onClick={fetchPrograms} sx={{ mt: 2 }}>
            Retry
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Manage Programs
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/programs/new')}
            sx={{ bgcolor: 'primary.main' }}
          >
            Create Program
          </Button>
        </Box>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search programs by name, term, or location..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {filteredPrograms.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {searchQuery ? 'No programs found matching your search' : 'No programs yet'}
            </Typography>
            {!searchQuery && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/admin/programs/new')}
                sx={{ mt: 2 }}
              >
                Create Your First Program
              </Button>
            )}
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Term</strong></TableCell>
                  <TableCell><strong>Price</strong></TableCell>
                  <TableCell><strong>Capacity (M/F)</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPrograms.map((program) => (
                  <TableRow key={program.id} hover sx={{ cursor: 'pointer' }}>
                    <TableCell onClick={() => navigate(`/admin/programs/${program.id}`)}>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {program.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {program.location}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={() => navigate(`/admin/programs/${program.id}`)}>{program.term}</TableCell>
                    <TableCell onClick={() => navigate(`/admin/programs/${program.id}`)}>${program.price.toFixed(2)}</TableCell>
                    <TableCell onClick={() => navigate(`/admin/programs/${program.id}`)}>
                      {program.male_capacity} / {program.female_capacity}
                    </TableCell>
                    <TableCell onClick={() => navigate(`/admin/programs/${program.id}`)}>
                      <Chip
                        label={program.public ? 'Public' : 'Private'}
                        color={program.public ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/programs/${program.id}/edit`);
                        }}
                        title="Quick Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(program.id, program.name);
                        }}
                        title="Delete Program"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Showing {filteredPrograms.length} of {programs.length} programs
        </Typography>
      </Container>
    </Box>
  );
}

export default AdminPrograms;
